import {
    call,
    delay,
    getContext,
    put,
    select,
    spawn,
    take,
    takeEvery,
    takeLatest,
} from 'redux-saga/effects';

import cartApi from '@elab/core/store/cart/api';
import dynaFormsApi from '@elab/core/utils/dynaForms/api';
import validation from '@elab/core/utils/validations';
import uiActions from '@elab/core/store/ui/actions';
import userSelectors from '@elab/core/store/user/selectors';
import * as configSelectors from '@elab/core/store/config/selectors';
import cartActions from '@elab/core/store/cart/actions';
import cartSelectors from '@elab/core/store/cart/selectors';
import cartTypes from '@elab/core/store/cart/types';
import dynaForms from '@elab/core/utils/dynaForms';
import analytics from '@elab/core/utils/analytics';
import logger from '@elab/core/utils/logger';
import CONSTANTS from '@elab/core/utils/constants';

import { errorTypes } from './constants';
import getActions from './actions';
import getSelectors from './selectors';
import getTypes from './types';
import { api } from './api';

export function* checkCardBalance(cardNumber) {
    const cardInfo = yield call(api.checkCardBalance, cardNumber);
    return {
        giftCard: cardInfo,
    };
}

const giftCardTogglerName = 'active';

export function* createGiftCardTransaction(cardNumber) {
    const cartGuid = yield select(state => cartSelectors.getGuid(state));
    const userId = yield select(userSelectors.getUserId);
    const cardInfo = yield call(
        cartApi.applyGiftcardOrVoucher,
        userId,
        cartGuid,
        CONSTANTS.giftcardApiUrl,
        cardNumber,
    );
    return cardInfo;
}

export function* createVoucherTransaction(submitUrl, cardNumber) {
    const userId = yield select(userSelectors.getUserId);
    const cartGuid = yield select(state => cartSelectors.getGuid(state));

    yield call(cartApi.applyGiftcardOrVoucher, userId, cartGuid, submitUrl, cardNumber);
}

export function* doSubmit({ giftCardNumber, voucherCode }, { actions, selectors }, submitUrl) {
    let cardInfo = {};
    const currentCard = giftCardNumber || voucherCode;
    const cardNumber = (currentCard.value || '').trim();
    const cardInputFieldName = currentCard.name;
    const isPayment = yield select(selectors.getIsPayment);
    const isVoucher = yield select(selectors.getIsVoucher);
    const isBalanceCheck = yield select(selectors.getIsBalanceCheck);

    yield put(actions.setSuccessMessageVisibility(false));

    if (isVoucher) {
        yield spawn(analytics.handlePromoAddSubmitEvent, cardNumber);
    }

    if (isBalanceCheck) {
        yield spawn(analytics.handleCheckGiftCardBalance, 1);
    }

    try {
        if (!cardNumber) {
            throw errorTypes.card;
        }

        if (isPayment) {
            cardInfo = yield call(createGiftCardTransaction, cardNumber);
        } else if (isVoucher) {
            yield call(createVoucherTransaction, submitUrl, cardNumber);
            yield put(cartActions.loadCart());
        } else {
            cardInfo = yield call(checkCardBalance, cardNumber);
        }

        yield put(uiActions.openDialog({ name: 'giftcard-is-valid-dialog' }));

        if (!(isVoucher || isBalanceCheck) && !cardInfo.giftCard.balance.value) {
            cardInfo = {};
            throw errorTypes.other;
        }

        if (isPayment) {
            yield spawn(analytics.handleGiftCardPayment, cardInfo.giftCard.cardNumber);
            yield put(cartActions.loadCart());
        }

        yield put(actions.setDataFieldValue(cardInputFieldName, '', true));

        if (isBalanceCheck) {
            yield spawn(analytics.handleCheckGiftCardBalance, 2);
        }

        yield put(actions.showSuccessMessage());

        if (isVoucher) {
            yield take(cartTypes.SET_CART_DATA);
            const couponDetails = yield select(cartSelectors.getCouponDetails);
            const voucherData = couponDetails.find(
                promotion => promotion.couponCode === cardNumber,
            );
            yield spawn(analytics.handlePromoAddConfirmEvent, {
                coupon: voucherData.couponCode,
                couponType: voucherData.percentageDiscountValue ? 'percentage' : 'fixed',
                couponAmount: voucherData.percentageDiscountValue || voucherData.value,
                discount: voucherData.value,
            });
        }
    } catch (e) {
        yield call(handleErrors, e, cardInputFieldName, cardInfo);
        if (isVoucher) {
            yield spawn(analytics.handlePromoAddErrorEvent, cardNumber);
        }
    } finally {
        yield put(actions.setCardInfo(cardInfo));
    }
}

export function* handleErrors(e, cardInputFieldName) {
    const { actions, selectors } = yield getContext('ctx');
    const scrollToError = yield select(selectors.getScrollToError);
    let errorText = '';
    const { type, message = '' } = (e.json && e.json.errors[0]) || {};
    if (e === errorTypes.card) {
        errorText = yield select(selectors.getErrorText, e);
    } else {
        errorText = e.message;
    }

    if (type) {
        yield put(actions.setDataFieldTypeError(cardInputFieldName, type));
    }

    yield put(actions.setDataFieldError(cardInputFieldName, errorText || message));

    if (scrollToError) {
        yield put(uiActions.scrollToCenter());
    }
}

export function* deleteGiftCard(cardNumber, cartGuid, userId) {
    const { actions, selectors } = yield getContext('ctx');

    const giftCardToBeDeleted = yield select(selectors.getGiftCardCodeToBeDeleted);

    if (giftCardToBeDeleted) {
        yield put(actions.setGiftCardCodeToBeDeleted(''));
        yield put(uiActions.closeDialog());
    }

    const cardCode = giftCardToBeDeleted || cardNumber;
    yield call(api.removeGiftCardTransaction, cartGuid, userId, cardCode);
}

export function* deleteItem({ payload }) {
    const { actions, selectors } = yield getContext('ctx');
    const cartGuid = yield select(cartSelectors.getGuid);
    const userId = yield select(userSelectors.getUserId);
    const isVoucher = yield select(selectors.getIsVoucher);

    try {
        if (isVoucher) {
            yield call(
                cartApi.removeGiftcardOrVoucher,
                userId,
                cartGuid,
                payload,
                CONSTANTS.voucherApiUrl,
            );
        } else {
            yield call(deleteGiftCard, payload, cartGuid, userId);
        }

        yield put(cartActions.loadCart());
        let isCartLoading = true;
        while (isCartLoading) {
            yield take(cartTypes.SET_LOADING);
            isCartLoading = yield select(cartSelectors.isLoading);
        }
        yield put(uiActions.scrollToTop({ target: 'e2-gift-card-form' }));
        yield put(actions.setSuccessMessageVisibility(false));

        if (isVoucher) {
            yield spawn(analytics.handlePromoRemoveEvent, payload);
        }
    } catch (e) {
        yield call(logger.error, e);
    }
}

export function* removeAllGiftCards() {
    const giftCardTransactions = yield select(cartSelectors.getGiftCardTransactions);
    if (!giftCardTransactions.length) return;

    const cartGuid = yield select(cartSelectors.getGuid);
    const userId = yield select(userSelectors.getUserId);

    try {
        yield call(api.removeGiftCardTransactions, cartGuid, userId);
        yield put(cartActions.loadCart());
    } catch (e) {
        yield call(logger.error, e);
    }
}

export function* togglersChange({ payload }) {
    if (payload.name === giftCardTogglerName && !payload.value) {
        yield call(removeAllGiftCards);
    }
}

export function* afterInitialize() {
    const { actions } = yield getContext('ctx');
    const isCartLoading = yield select(cartSelectors.isLoading);
    const cardData = yield select(cartSelectors.getData);
    if (isCartLoading || !cardData.giftCardTransactions) {
        yield take(cartTypes.SET_CART_DATA);
    }

    const giftCardTransactions = yield select(cartSelectors.getGiftCardTransactions);
    const isActiveOpened = !!giftCardTransactions.length;
    yield put(
        actions.toggleFieldValue({
            name: giftCardTogglerName,
            value: isActiveOpened,
        }),
    );

    let params = {};
    const giftCardFormConfig = yield select(configSelectors.getGiftCardFormConfig);
    if (giftCardFormConfig.giftCardAmountForm) {
        try {
            const uri = yield call(
                dynaFormsApi.getFormMetadata,
                giftCardFormConfig.giftCardAmountForm,
            );
            const { steps = [] } = yield call(dynaFormsApi.loadMetadata, uri);
            const { fields = [] } = steps[0] || {};
            params = fields[0].constraints ? fields[0].constraints[0].parameters : {};
        } catch (e) {
            /* Nothing */
        }
    }

    yield put(actions.setValidationParams(params));
}

export function* setProperties({ payload }) {
    if (payload.isBalanceCheck) {
        yield spawn(analytics.handleCheckGiftCardBalance, 0);
    }
}

export function* validateGiftCardAmount(payload) {
    const { selectors } = yield getContext('ctx');

    const { cardBalance, amountToUse } = payload;
    const cartTotal = payload.cartTotal.value;
    const params = yield select(selectors.getValidationParams);

    return (
        validation.pattern(amountToUse, '', params) &&
        amountToUse !== '' &&
        amountToUse <= cardBalance &&
        amountToUse <= cartTotal
    );
}

export function* submitAmountChange(cardNumber, amountToUse) {
    const userId = yield select(userSelectors.getUserId);
    const cartId = yield select(cartSelectors.getCartId);

    try {
        yield call(api.updateGiftCardAmount, userId, cartId, cardNumber, amountToUse);
    } catch (e) {
        throw new Error(`Failed updating gift card amount: ${e})`);
    }
}

export function* updateGiftCardAmount({ payload }) {
    const { selectors, actions } = yield getContext('ctx');

    const validInput = yield call(validateGiftCardAmount, payload);
    let cardsWithError = yield select(selectors.getCardsWithError);
    const currentCardHasError = yield select(selectors.getCardHasError, payload.cardNumber);
    if (!validInput) {
        if (!currentCardHasError) {
            yield put(actions.setCardsWithError([...cardsWithError, payload.cardNumber]));
        }
    } else {
        if (currentCardHasError && cardsWithError.length > 0) {
            cardsWithError = cardsWithError.filter(cardNum => cardNum !== payload.cardNumber);

            yield put(actions.setCardsWithError(cardsWithError));
        }

        try {
            yield call(submitAmountChange, payload.cardNumber, payload.amountToUse);
            yield put(cartActions.loadCart());
        } catch {
            yield put(actions.setCardsWithError([...cardsWithError, payload.cardNumber]));
        }
    }
}

export function* askRemovalConfirmation({ payload }) {
    const { actions } = yield getContext('ctx');

    yield put(actions.setGiftCardCodeToBeDeleted(payload.number));
    yield put(uiActions.openDialog({ name: payload.dialogName }));
}

export function* showSuccessMessage() {
    const { actions } = yield getContext('ctx');
    yield put(actions.setSuccessMessageVisibility(true));
    yield delay(5000);
    yield put(actions.setSuccessMessageVisibility(false));
}

// FormId is used to create pure dynaform as in MRN for vouchers form
export default (storeName, formId) => () => {
    const actions = getActions(storeName);
    const selectors = getSelectors(storeName);
    const types = getTypes(storeName);
    const ctx = { actions, selectors, types, storeName };
    return [
        ...dynaForms.createSagaFor(
            ctx,
            formId ? dynaFormsApi.getFormMetadata(formId) : require('./metadata.js').default,
            doSubmit,
        ),
        takeEvery(types.DELETE_ITEM, deleteItem),
        takeEvery(types.TOGGLE_FIELD_VALUE, togglersChange),
        takeEvery(types.AFTER_INITIALIZE, afterInitialize),
        takeEvery(types.SET_PROPERTIES, setProperties),
        takeEvery(types.UPDATE_GIFT_CARD_AMOUNT, updateGiftCardAmount),
        takeEvery(types.ASK_REMOVAL_CONFIRMATION, askRemovalConfirmation),
        takeLatest(types.SHOW_SUCCESS_MESSAGE, showSuccessMessage),
    ];
};
